import { useState, useCallback } from "react";
import {
  AppInput,
  AppModal,
  AppMultiAsync,
  AppNoInfo,
  AppRemixIcon,
} from "@/layout/AppComponents";
import { Formik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import ReactDOMServer from "react-dom/server";
import Translator from "../../../../../components/helpers/Translator";
import { axiosPath } from "../../../../../../routing/helpers/router";
import colors from "../../../../../../constants/colors";
import useIsMounted from "../../../../../components/hooks/useIsMounted";

const PlantAutoCloneElement = (props) => {
  const { autoClone } = props;
  const { moduleBridgeHex } = useParams();
  const isMounted = useIsMounted();
  const auth = useSelector((state) => state.auth);
  const [showModal, setShowModal] = useState(false);
  const [isCloning, setIsCloning] = useState(false);
  const [usedPlantName, setUsedPlantName] = useState(false);
  const [locationRequired, setLocationRequired] = useState(false);

  const loadAsyncLocations = useCallback(
    async (payload) => {
      const options = await axios
        .post(
          axiosPath(
            "api_r_user_module_legalCompliance_plantSelect_autoClone_selectLocations"
          ),
          {
            payload,
            autoCloneID: autoClone?.id,
          }
        )
        .then((r) => r.data)
        .catch(() => []);
      return options;
    },
    [autoClone]
  );

  const handleAutoClone = useCallback(
    async (payload) => {
      setIsCloning(true);
      setUsedPlantName(false);
      setLocationRequired(false);
      await axios
        .post(
          axiosPath(
            "api_r_user_module_legalCompliance_plantSelect_autoClone_clone"
          ),
          {
            autoCloneID: autoClone.id,
            payload,
            moduleBridgeHex,
          }
        )
        .then(() => {
          if (isMounted.current) {
            location.reload();
          }
        })
        .catch((e) => {
          if (e?.response?.status === 412) {
            setUsedPlantName(true);
          }
          if (e?.response?.status === 417) {
            setLocationRequired(true);
          }
          setIsCloning(false);
        });
    },
    [isCloning]
  );

  return (
    <li className="sidebar-element">
      <span className="sidebar-element" onClick={() => setShowModal(true)}>
        <AppRemixIcon icon="file-copy" className="sidebar-icon pr-1" />
        <span className="sb-title">{autoClone.name}</span>
      </span>
      <AppModal
        title={autoClone.name}
        show={showModal}
        onClose={() => setShowModal(false)}
      >
        {!isCloning ? (
          <Formik
            initialValues={{
              name: "",
              locations: [],
            }}
            validationSchema={() =>
              Yup.lazy(() => {
                if (autoClone?.settings?.clonePlantName?.type === 0) {
                  return Yup.object().shape({
                    name: Yup.string().required(
                      ReactDOMServer.renderToString(
                        <Translator
                          file="module-legalCompliance"
                          name="required"
                        />
                      )
                    ),
                  });
                }
                return Yup.object();
              })
            }
            onSubmit={handleAutoClone}
          >
            {({
              setFieldValue,
              handleSubmit,
              values,
              errors,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                {isSubmitting ? (
                  <AppNoInfo />
                ) : (
                  <>
                    {autoClone?.limited && (
                      <Translator
                        file="module-legalCompliance"
                        name="cannot.create.evaluations.you.need.deactivate.existing.one"
                      />
                    )}
                    {usedPlantName && (
                      <p className="text-danger">
                        <Translator
                          file="module-legalCompliance"
                          name="selected-name-already-exists-module"
                        />
                      </p>
                    )}
                    {autoClone?.settings?.clonePlantName?.type === 0 ? (
                      <AppInput
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-legalCompliance"
                            name="name.new.installation"
                          />
                        )}
                        icon={<AppRemixIcon icon="file-copy" />}
                        value={values.name}
                        errors={values.name}
                        onChange={(opt) => setFieldValue("name", opt)}
                        error={errors?.name || usedPlantName}
                        isClearable
                        isReadOnly={autoClone?.limited}
                        required
                      />
                    ) : (
                      <AppInput
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-legalCompliance"
                            name="name.new.installation"
                          />
                        )}
                        icon={<AppRemixIcon icon="file-copy" />}
                        value={`${
                          autoClone.settings?.clonePlantName?.type === 1
                            ? autoClone.templatePlantName
                            : auth?.user?.user
                        } ${
                          autoClone.settings?.clonePlantName?.type === 3
                            ? autoClone.settings?.clonePlantName?.sufix
                            : ""
                        } [${autoClone?.autoClonePlantCount + 1}]`}
                        isClearable
                        isReadOnly
                      />
                    )}
                    {autoClone?.allowSelectLocation && (
                      <AppMultiAsync
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-legalCompliance"
                            name="select.location.s"
                          />
                        )}
                        icon={<AppRemixIcon icon="globe" />}
                        asyncFunction={loadAsyncLocations}
                        value={values.locations}
                        error={locationRequired}
                        onChange={(opt) => {
                          setFieldValue("locations", opt);
                        }}
                        setLabelField="name"
                      />
                    )}

                    {autoClone?.limited ? (
                      <span
                        className="btn btn-outline-light btn-block mt-3 disabled"
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-legalCompliance"
                            name="you-have-reached-limit"
                          />
                        )}
                      >
                        <Translator
                          file="module-legalCompliance"
                          name="create.new.installation"
                        />
                      </span>
                    ) : (
                      <button
                        className="btn btn-outline-light btn-block mt-3"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        <Translator
                          file="module-legalCompliance"
                          name="create.new.installation"
                        />
                      </button>
                    )}
                  </>
                )}
              </form>
            )}
          </Formik>
        ) : (
          <AppNoInfo
            text={
              <Translator
                file="module-legalCompliance"
                name="creating.new.installation"
              />
            }
            minHeight={200}
            color={colors.grey500}
          />
        )}
      </AppModal>
    </li>
  );
};

export default PlantAutoCloneElement;
